// src/component/PromoBannerLauncher.js
//
// サイト上部プッシュ通知のロジックをまとめたフック＋アイコン用コンポーネント。
// - usePromoBanner(): systemConfig/promoBanner と users/{uid}.promoBannerState を
//   購読し、表示可否・ダイアログ開閉・「今後表示しない」操作を返す。
// - <PromoBannerDialogMount /> : フックの状態に従ってダイアログを描画する。
// - <PromoBannerRailIcon />    : 左レール用アイコン（Tooltip + Badge付き）
// - <PromoBannerFloatingIcon />: モバイル右下フローティング用アイコン（揺れる）
//
// 配置は AppShell.js から行う（左レール／モバイルフローティング／ダイアログ）。

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Badge, IconButton, Tooltip } from "@mui/material";
import { keyframes } from "@mui/system";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { auth, db } from "../api/firebase";
import { ensureServerTimeSynced, serverNowMs } from "../api/serverTime";
import {
  PROMO_BANNER_CONFIG_DOC,
  PROMO_BANNER_DEFAULTS,
  PROMO_BANNER_USER_STATE_FIELD,
  PROMO_BANNER_AUDIENCE,
} from "./promoBannerDefaults";
import PromoBannerDialog from "./PromoBannerDialog";

function todayKey() {
  const d = new Date(serverNowMs());
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function isExpired(expiresAt) {
  if (!expiresAt || typeof expiresAt !== "string") return false;
  const m = expiresAt.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/
  );
  if (!m) return false;
  const date = new Date(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
    Number(m[4]),
    Number(m[5]),
    m[6] ? Number(m[6]) : 0,
    0
  );
  return serverNowMs() > date.getTime();
}

function userHasAnyPhase(payments, phases) {
  if (!Array.isArray(payments) || payments.length === 0) return false;
  if (!Array.isArray(phases) || phases.length === 0) return false;
  return payments.some((p) => {
    const ph = p?.paymentPhase != null ? String(p.paymentPhase).trim() : "";
    return ph && phases.includes(ph);
  });
}

function isUserInAudience(audience, { isAdmin, payments }) {
  if (isAdmin) return true;
  const mode = audience?.mode || PROMO_BANNER_AUDIENCE.ADMIN;
  if (mode === PROMO_BANNER_AUDIENCE.ALL) return true;
  if (mode === PROMO_BANNER_AUDIENCE.ADMIN) return false;
  if (mode === PROMO_BANNER_AUDIENCE.PHASES) {
    return userHasAnyPhase(payments, audience?.phases || []);
  }
  return false;
}

/**
 * usePromoBanner — 表示状態と操作 API を返す。
 *
 * 戻り値:
 *   - ready          : Firestore 購読が一度返ってきたか
 *   - shouldShow     : アイコン／ダイアログを出してよいか（公開範囲・期限・「今後表示しない」を考慮）
 *   - tooltipText    : ツールチップ文言
 *   - config         : 表示用の設定（title / body / imageUrl / linkUrl / chipLabel など）
 *   - dialogOpen     : ダイアログの open 状態
 *   - openDialog()   : アイコンクリック等で手動オープン
 *   - closeDialog()  : 「閉じる」（その日の自動表示は止める）
 *   - dismissForever(): 「今後表示しない」（version を保存）
 */
export function usePromoBanner() {
  const [config, setConfig] = useState(null);
  const [configReady, setConfigReady] = useState(false);

  const [userState, setUserState] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [payments, setPayments] = useState([]);
  const [userReady, setUserReady] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);

  // 今セッションで自動オープンを試みたか
  const autoOpenTriedRef = useRef(false);

  useEffect(() => {
    ensureServerTimeSynced();
  }, []);

  // systemConfig/promoBanner を購読
  useEffect(() => {
    const ref = doc(db, "systemConfig", PROMO_BANNER_CONFIG_DOC);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setConfig({
            ...PROMO_BANNER_DEFAULTS,
            ...data,
            audience: {
              ...PROMO_BANNER_DEFAULTS.audience,
              ...(data.audience || {}),
            },
          });
        } else {
          setConfig({ ...PROMO_BANNER_DEFAULTS });
        }
        setConfigReady(true);
      },
      (err) => {
        console.error("usePromoBanner config error:", err);
        setConfig({ ...PROMO_BANNER_DEFAULTS });
        setConfigReady(true);
      }
    );
    return () => unsub();
  }, []);

  // users/{uid} を購読（isAdmin / payments / promoBannerState）
  useEffect(() => {
    const current = auth.currentUser;
    if (!current) {
      setUserState(null);
      setIsAdmin(false);
      setPayments([]);
      setUserReady(true);
      return;
    }
    const ref = doc(db, "users", current.uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data() || {};
          setUserState(data[PROMO_BANNER_USER_STATE_FIELD] || null);
          setIsAdmin(!!data.isAdmin);
          setPayments(Array.isArray(data.payments) ? data.payments : []);
        } else {
          setUserState(null);
          setIsAdmin(false);
          setPayments([]);
        }
        setUserReady(true);
      },
      (err) => {
        console.error("usePromoBanner user error:", err);
        setUserState(null);
        setIsAdmin(false);
        setPayments([]);
        setUserReady(true);
      }
    );
    return () => unsub();
  }, []);

  // バージョン更新やユーザー状態の変化で「セッション内自動オープン済み」をリセット
  useEffect(() => {
    autoOpenTriedRef.current = false;
  }, [config?.version, userState?.dismissedVersion]);

  const ready = configReady && userReady;

  const adminBypass = isAdmin && config?.adminBypassLimits !== false;

  // アイコン表示用の判定（dismiss は含めない）。
  // 「今後表示しない」を押してもアイコンは残るので、手動でいつでも開ける。
  const shouldShow = useMemo(() => {
    if (!ready) return false;
    if (!config || !config.enabled) return false;
    if (isExpired(config.expiresAt)) return false;
    if (!isUserInAudience(config.audience, { isAdmin, payments })) return false;
    return true;
  }, [ready, config, isAdmin, payments]);

  // 「今後表示しない」を押したか（自動オープンを抑制するためだけに使う）
  const isDismissed = useMemo(() => {
    if (!config) return false;
    const version = Number(config.version || 1);
    const dismissedVersion = Number(userState?.dismissedVersion || 0);
    return dismissedVersion >= version;
  }, [config, userState]);

  const persistUserState = useCallback(
    async (patch) => {
      const current = auth.currentUser;
      if (!current) return;
      const ref = doc(db, "users", current.uid);
      await setDoc(
        ref,
        {
          [PROMO_BANNER_USER_STATE_FIELD]: {
            ...(userState || {}),
            ...patch,
          },
        },
        { merge: true }
      );
    },
    [userState]
  );

  // 日次自動オープン
  //  - 管理者バイパス時は毎回オープン（テスト用）
  //  - そうでない場合：「今後表示しない」していれば自動オープンしない（アイコンからは手動で開ける）
  //  - そうでない場合：本日すでに自動表示済みなら出さない
  useEffect(() => {
    if (!shouldShow) return;
    if (autoOpenTriedRef.current) return;

    if (!adminBypass) {
      if (isDismissed) {
        autoOpenTriedRef.current = true;
        return;
      }
      const today = todayKey();
      if (userState?.lastShownDate === today) {
        autoOpenTriedRef.current = true;
        return;
      }
    }

    autoOpenTriedRef.current = true;
    setDialogOpen(true);

    if (!adminBypass) {
      persistUserState({ lastShownDate: todayKey() }).catch((err) =>
        console.error("usePromoBanner lastShownDate update error:", err)
      );
    }
  }, [shouldShow, userState, persistUserState, adminBypass, isDismissed]);

  const openDialog = useCallback(() => {
    if (!shouldShow) return;
    setDialogOpen(true);
    // 手動で開いたときも本日表示済みとして記録（管理者バイパス時は記録不要）
    if (!adminBypass) {
      persistUserState({ lastShownDate: todayKey() }).catch((err) =>
        console.error("usePromoBanner manual open update error:", err)
      );
    }
  }, [shouldShow, persistUserState, adminBypass]);

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const dismissForever = useCallback(async () => {
    setDialogOpen(false);
    try {
      await persistUserState({
        dismissedVersion: Number(config?.version || 1),
        dismissedAt: new Date().toISOString(),
      });
    } catch (err) {
      console.error("usePromoBanner dismiss error:", err);
    }
  }, [config?.version, persistUserState]);

  return {
    ready,
    shouldShow,
    tooltipText:
      config?.tooltipText || PROMO_BANNER_DEFAULTS.tooltipText,
    config,
    dialogOpen,
    openDialog,
    closeDialog,
    dismissForever,
  };
}

/**
 * 左レール用：丸いアイコン + Tooltip + 通知ドット
 */
export function PromoBannerRailIcon({
  tooltipText,
  onClick,
  theme,
}) {
  const accent = theme?.railActive ?? "#5A35FF";
  const surface = theme?.railItemBg ?? "rgba(255,255,255,0.96)";
  return (
    <Tooltip
      title={tooltipText}
      placement="right"
      arrow
      enterTouchDelay={0}
    >
      <Badge
        color="error"
        variant="dot"
        overlap="circular"
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mb: 1 }}
      >
        <IconButton
          onClick={onClick}
          aria-label="お知らせを開く"
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            bgcolor: surface,
            boxShadow: "0 8px 20px rgba(30,12,90,0.08)",
          }}
        >
          <CampaignRoundedIcon sx={{ fontSize: 22, color: accent }} />
        </IconButton>
      </Badge>
    </Tooltip>
  );
}

// 数秒に一度、横揺れを繰り返すアニメ（おおよそ10往復）
const wiggleAnim = keyframes`
  0%   { transform: translateX(0); }
  2%   { transform: translateX(-4px); }
  4%   { transform: translateX(4px); }
  6%   { transform: translateX(-3px); }
  8%   { transform: translateX(3px); }
  10%  { transform: translateX(-3px); }
  12%  { transform: translateX(3px); }
  14%  { transform: translateX(-2px); }
  16%  { transform: translateX(2px); }
  18%  { transform: translateX(-1px); }
  20%  { transform: translateX(0); }
  100% { transform: translateX(0); }
`;

/**
 * モバイル用フローティング：揺れる丸ボタン + 通知ドット
 * AppShell が `position: fixed` で配置する想定。
 */
export function PromoBannerFloatingIcon({
  tooltipText,
  onClick,
  theme,
}) {
  const accent = theme?.railActive ?? "#5A35FF";
  return (
    <Tooltip title={tooltipText} placement="left" arrow enterTouchDelay={0}>
      <Badge
        color="error"
        variant="dot"
        overlap="circular"
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <IconButton
          onClick={onClick}
          aria-label="お知らせを開く"
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            bgcolor: theme?.bottomBg ?? "rgba(255,255,255,0.9)",
            border: `1px solid ${theme?.bottomBorder ?? "rgba(0,0,0,0.1)"}`,
            boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
            animation: `${wiggleAnim} 5s ease-in-out infinite`,
            transformOrigin: "center",
          }}
        >
          <CampaignRoundedIcon sx={{ fontSize: 22, color: accent }} />
        </IconButton>
      </Badge>
    </Tooltip>
  );
}

/**
 * AppShell からマウントするダイアログ。
 */
export function PromoBannerDialogMount({ promo }) {
  if (!promo?.config) return null;
  return (
    <PromoBannerDialog
      open={promo.dialogOpen}
      chipLabel={promo.config.chipLabel}
      title={promo.config.title}
      body={promo.config.body}
      imageUrl={promo.config.imageUrl}
      linkUrl={promo.config.linkUrl}
      linkLabel={promo.config.linkLabel}
      onCloseToday={promo.closeDialog}
      onDismissForever={promo.dismissForever}
    />
  );
}

// この Launcher は古い互換のため空マウントだけにする（重複表示を避ける）。
// アイコン／ダイアログは AppShell 側でマウントする。
export default function PromoBannerLauncher() {
  return null;
}

// AppShell が一括で使う Composite Provider
export function PromoBannerHost({ children, render }) {
  const promo = usePromoBanner();
  return render(promo, children);
}
